<script lang="ts">
  import { count, today } from '../../utils/insights';
  import {
    BODY_COLOR, BODY_KINDS, HEART_COLOR, READING_NAMES, SLEEP_COLOR, STEPS_COLOR, VITAL_KINDS, VITALS_COLOR,
    hourLabel, readingValue, short, sleepTime, stageSeconds, type HealthDay, type HealthReading, type ReadingKind,
  } from '../../utils/health';
  import { integer, t, time } from '../../i18n/index.ts';
  import HealthCard from './HealthCard.svelte';
  import BarChart from './BarChart.svelte';
  import PointChart from './PointChart.svelte';
  import SleepTimeline from './SleepTimeline.svelte';

  // DayView is the Day tab (spec 007 FR-019): Steps, Sleep, Heart rate,
  // Body, and Vitals cards, each left out when the day has none of it
  // (007 FR-018).
  let { day }: { day: HealthDay } = $props();

  const HOURS = Array.from({ length: 24 }, (_, h) => h);
  const HOUR_NAMES = HOURS.map(hourLabel);
  const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => ({ at: h / 24, text: hourLabel(h % 24) }));
  const clock = (iso: string) => time(new Date(iso));
  const bpm = (v: number) => t('healthBpm', { bpm: integer(Math.round(v)) });

  let isToday = $derived(day.date === today());
  let steps = $derived(day.steps ?? (day.step_hours.length ? day.step_hours.reduce((sum, h) => sum + h.steps, 0) : undefined));
  let stepHours = $derived(HOURS.map((h) => day.step_hours.find((x) => x.hour === h)?.steps ?? null));

  let asleep = $derived(day.sleep.reduce((sum, s) => sum + s.asleep_s, 0));
  let staged = $derived(day.sleep.some((s) => s.stages.length > 0));
  let deep = $derived(stageSeconds(day.sleep, 'deep'));

  let latest = $derived(day.hr_hours.length ? day.hr_hours[day.hr_hours.length - 1] : null);
  let hrAvg = $derived(HOURS.map((h) => day.hr_hours.find((x) => x.hour === h)?.avg ?? null));
  let hrBand = $derived(HOURS.map((h) => {
    const x = day.hr_hours.find((r) => r.hour === h);
    return x ? { lo: x.min, hi: x.max } : null;
  }));

  const readingsOf = (kinds: ReadingKind[]) => day.readings.filter((r) => kinds.includes(r.kind));
  let body = $derived(readingsOf(BODY_KINDS));
  let vitals = $derived(readingsOf(VITAL_KINDS));
  let empty = $derived(steps == null && day.sleep.length === 0 && !latest && day.resting_hr == null && day.readings.length === 0);
</script>

{#snippet readingList(rs: HealthReading[])}
  <ul class="divide-y" style="border-color: var(--theme-surface1);">
    {#each rs as r, k (k)}
      <li class="flex items-baseline gap-3 py-1.5 text-sm">
        <span class="flex-1">{READING_NAMES[r.kind]}</span>
        <span class="font-semibold">{readingValue(r.kind, r.value, r.value2)}</span>
        <span class="text-xs text-text-muted w-20 text-right">{clock(r.at)}</span>
      </li>
    {/each}
  </ul>
{/snippet}

{#if empty}
  <p class="text-sm text-text-muted">{t('healthNothingDay')}</p>
{:else}
  <div class="grid gap-4 md:grid-cols-2 items-start">
    {#if steps != null}
      <HealthCard title={t('healthSteps')} icon="la-shoe-prints" color={STEPS_COLOR} label={isToday ? t('healthToday') : t('healthTotal')} value={count(steps)}
        typical={day.typical_steps != null ? short(day.typical_steps) : undefined}>
        {#if day.step_hours.length}
          <BarChart values={stepHours} color={STEPS_COLOR} typicalSeries={day.typical_step_hours.length ? day.typical_step_hours.map((h) => h.steps) : undefined}
            ticks={HOUR_TICKS} names={HOUR_NAMES} caption={t('healthStepsByHour')} format={count} unit={t('healthUnitSteps')} tick={short}
            heading={{ name: t('healthHour'), value: t('healthSteps'), typical: day.typical_step_hours.length ? t('healthTypical') : undefined }} />
        {:else}
          <p class="text-xs text-text-muted">{t('healthStepsNotKept')}</p>
        {/if}
      </HealthCard>
    {/if}

    {#if day.sleep.length}
      <HealthCard title={t('healthSleep')} icon="la-bed" color={SLEEP_COLOR} label={t('healthTotal')} value={sleepTime(asleep)}
        typical={day.typical_asleep_s != null ? sleepTime(day.typical_asleep_s) : undefined}
        side={staged ? { label: t('healthDeepSleep'), value: sleepTime(deep) } : undefined}
        details={[
          { label: t('healthFallAsleep'), value: clock(day.sleep[0].started_at) },
          { label: t('healthWakeUp'), value: clock(day.sleep[day.sleep.length - 1].ended_at) },
          ...(day.sleep.length > 1 ? [{ label: t('healthSessions'), value: integer(day.sleep.length) }] : []),
        ]}>
        <SleepTimeline date={day.date} sleep={day.sleep} />
      </HealthCard>
    {/if}

    {#if latest || day.resting_hr != null}
      <HealthCard title={t('healthHeartRate')} icon="la-heartbeat" color={HEART_COLOR} label={t('healthLatest')} value={latest ? integer(latest.avg) : undefined} unit={t('healthUnitBpm')}
        side={day.resting_hr != null ? { label: t('healthResting'), value: integer(day.resting_hr) } : undefined}
        details={day.hr_hours.length ? [
          { label: t('healthLowest'), value: bpm(Math.min(...day.hr_hours.map((h) => h.min))) },
          { label: t('healthHighest'), value: bpm(Math.max(...day.hr_hours.map((h) => h.max))) },
        ] : []}>
        {#if day.hr_hours.length}
          <PointChart series={[{ name: t('healthAverage'), color: HEART_COLOR, values: hrAvg }]} band={hrBand} bandName={t('healthLowestToHighest')}
            ticks={HOUR_TICKS} names={HOUR_NAMES} column={t('healthHour')} caption={t('healthHeartByHour')}
            format={bpm} unit={t('healthUnitBpm')} />
        {/if}
      </HealthCard>
    {/if}

    {#if body.length}
      <HealthCard title={t('healthBody')} icon="la-weight" color={BODY_COLOR}>
        {@render readingList(body)}
      </HealthCard>
    {/if}

    {#if vitals.length}
      <HealthCard title={t('healthVitals')} icon="la-stethoscope" color={VITALS_COLOR}>
        {@render readingList(vitals)}
      </HealthCard>
    {/if}
  </div>
{/if}
